'use client';

import { c as _c } from "react/compiler-runtime";
import { useInView } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { RefObject, useRef } from 'react';
import useSWR from 'swr';
import { useHover, useIsClient } from 'usehooks-ts';
import YoutubeCardDialog from '@/app/[locale]/(create)/components/YoutubeCardDialog';
import { useDraggingScroll } from '@/hooks/useDraggingScroll';
import { useScrollable } from '@/hooks/useScrollable';
import { cn } from '@/lib/utils';
import { getCreatorTutorial } from '@/services/cms';
function Tutorials() {
  const $ = _c(73);
  const ref = useRef(null);
  const isHovered = useHover(ref as RefObject<HTMLElement>);
  const t = useTranslations();
  const isClient = useIsClient();
  const locale = useLocale();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      once: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isInView = useInView(ref, t0);
  let t1;
  if ($[1] !== locale) {
    t1 = () => getCreatorTutorial(locale, false).then(_temp);
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: creatorTutorialData,
    isLoading
  } = useSWR("creatorTutorial", t1);
  const {
    containerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight
  } = useScrollable(undefined, undefined, isLoading);
  const {
    bind,
    disableDrag,
    isDragging,
    onMouseDown,
    onMouseUp
  } = useDraggingScroll(containerRef);
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== bind || $[4] !== canScrollLeft || $[5] !== canScrollRight || $[6] !== containerRef || $[7] !== creatorTutorialData?.results || $[8] !== disableDrag || $[9] !== isClient || $[10] !== isDragging || $[11] !== isHovered || $[12] !== isInView || $[13] !== onMouseDown || $[14] !== onMouseUp || $[15] !== scrollLeft || $[16] !== scrollRight || $[17] !== t) {
    const tutorials = (creatorTutorialData?.results || []).map(_temp2);
    t8 = "tutorials";
    t9 = ref;
    t10 = "1.5xl:pb-[80px] 1.5xl:pt-[40px] 3xl:px-[120px] relative flex flex-1 scroll-mt-20 flex-col justify-center gap-[48px] px-4 py-8 md:gap-[80px] md:px-[40px]";
    let t12;
    if ($[28] !== t) {
      t12 = t("creator-intro.tutorials-title");
      $[28] = t;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    if ($[30] !== t12) {
      t11 = <div className="header-3 md:header-2 text-center">{t12}</div>;
      $[30] = t12;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t2 = "relative flex w-full flex-1 gap-6 overflow-hidden rounded-t-2xl";
    const t13 = isHovered && canScrollRight;
    const t14 = !canScrollRight;
    let t15;
    if ($[32] !== t13 || $[33] !== t14) {
      t15 = cn("1.5xl:w-[622px] pointer-events-none absolute right-[-10%] z-interactive h-full w-1/2 transition-opacity duration-300", {
        "opacity-100": t13,
        "opacity-0": t14
      });
      $[32] = t13;
      $[33] = t14;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        background: "linear-gradient(270deg, #121216 11.09%, rgba(18, 18, 22, 0) 100%)"
      };
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    if ($[36] !== t15) {
      t3 = <div className={t15} style={t16} />;
      $[36] = t15;
      $[37] = t3;
    } else {
      t3 = $[37];
    }
    const t17 = isHovered && canScrollRight;
    const t18 = !canScrollRight;
    let t19;
    if ($[38] !== t17 || $[39] !== t18) {
      t19 = cn("group bg-vive-primary-70 absolute top-1/3 right-5 z-floating hidden size-8 -translate-y-1/2 cursor-pointer place-items-center rounded-full opacity-0 transition-opacity duration-300 md:grid md:size-[52px]", {
        "opacity-100": t17,
        "opacity-0": t18
      });
      $[38] = t17;
      $[39] = t18;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    let t20;
    if ($[41] !== scrollRight) {
      t20 = <ChevronRight onClick={scrollRight} className="stroke-vive-primary-30 group-hover:stroke-vive-primary-0 size-[36px] transition-all" />;
      $[41] = scrollRight;
      $[42] = t20;
    } else {
      t20 = $[42];
    }
    if ($[43] !== scrollRight || $[44] !== t19 || $[45] !== t20) {
      t4 = <div className={t19} onClick={scrollRight}>{t20}</div>;
      $[43] = scrollRight;
      $[44] = t19;
      $[45] = t20;
      $[46] = t4;
    } else {
      t4 = $[46];
    }
    const t21 = !canScrollLeft;
    let t22;
    if ($[47] !== canScrollLeft || $[48] !== t21) {
      t22 = cn("1.5xl:w-[622px] pointer-events-none absolute left-[-10%] z-interactive h-full w-1/2 transition-opacity duration-300", {
        "opacity-100": canScrollLeft,
        "opacity-0": t21
      });
      $[47] = canScrollLeft;
      $[48] = t21;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = {
        background: "linear-gradient(90deg, #121216 11.09%, rgba(18, 18, 22, 0) 100%)"
      };
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    if ($[51] !== t22) {
      t5 = <div className={t22} style={t23} />;
      $[51] = t22;
      $[52] = t5;
    } else {
      t5 = $[52];
    }
    const t24 = isHovered && canScrollLeft;
    const t25 = !canScrollLeft;
    let t26;
    if ($[53] !== t24 || $[54] !== t25) {
      t26 = cn("group bg-vive-primary-70 absolute top-1/3 left-5 z-floating hidden size-8 -translate-y-1/2 cursor-pointer place-items-center rounded-full opacity-0 transition-opacity duration-300 md:grid md:size-[52px]", {
        "opacity-100": t24,
        "opacity-0": t25
      });
      $[53] = t24;
      $[54] = t25;
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    let t27;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <ChevronLeft className="stroke-vive-primary-30 group-hover:stroke-vive-primary-0 size-[36px] transition-all" />;
      $[56] = t27;
    } else {
      t27 = $[56];
    }
    if ($[57] !== scrollLeft || $[58] !== t26) {
      t6 = <div className={t26} onClick={scrollLeft}>{t27}</div>;
      $[57] = scrollLeft;
      $[58] = t26;
      $[59] = t6;
    } else {
      t6 = $[59];
    }
    t7 = isClient && <div {...bind()} ref={containerRef} className={cn("no-scrollbar flex w-full flex-1 gap-6 overflow-auto scroll-smooth rounded-t-2xl select-none active:cursor-grabbing", {
      "cursor-grab": !disableDrag,
      "opacity-0": !isInView
    })} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>{tutorials.map(tutorial_0 => <YoutubeCardDialog key={tutorial_0.title} {...tutorial_0} isDragging={isDragging} />)}</div>;
    $[3] = bind;
    $[4] = canScrollLeft;
    $[5] = canScrollRight;
    $[6] = containerRef;
    $[7] = creatorTutorialData?.results;
    $[8] = disableDrag;
    $[9] = isClient;
    $[10] = isDragging;
    $[11] = isHovered;
    $[12] = isInView;
    $[13] = onMouseDown;
    $[14] = onMouseUp;
    $[15] = scrollLeft;
    $[16] = scrollRight;
    $[17] = t;
    $[18] = t10;
    $[19] = t11;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t2 = $[20];
    t3 = $[21];
    t4 = $[22];
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t12;
  if ($[60] !== t2 || $[61] !== t3 || $[62] !== t4 || $[63] !== t5 || $[64] !== t6 || $[65] !== t7) {
    t12 = <div className={t2}>{t3}{t4}{t5}{t6}{t7}</div>;
    $[60] = t2;
    $[61] = t3;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
    $[65] = t7;
    $[66] = t12;
  } else {
    t12 = $[66];
  }
  let t13;
  if ($[67] !== t10 || $[68] !== t11 || $[69] !== t12 || $[70] !== t8 || $[71] !== t9) {
    t13 = <section id={t8} ref={t9} className={t10}>{t11}{t12}</section>;
    $[67] = t10;
    $[68] = t11;
    $[69] = t12;
    $[70] = t8;
    $[71] = t9;
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  return t13;
}
function _temp2(tutorial) {
  return {
    title: tutorial.title,
    description: tutorial.description,
    thumbnail: tutorial.image,
    embedLink: tutorial.action_link
  };
}
function _temp(res) {
  return res?.results;
}
export default Tutorials;